import React, { useEffect, useMemo, useState } from "react";
import { exportCompetitionMatchesExcel, exportCompetitionMatchesPdf, userFullName } from "../../api";
import { MatchEditModal } from "../../components/common/tournament-event/MatchEditModal";
import { GlobalMatchesExportModal, GlobalMatchesExportRow } from "../../components/common/GlobalMatchesExportModal";
import { Button } from "../../components/common/Button";
import { ModalSelectSeason } from "../../components/common/ModalSelectSeason";
import { useAuth } from "../../contexts/AuthContext";
import { Match } from "../../types";
import { currentTenant } from "../../tenants";
import { downloadBlob } from "../../utils/download";
import { formatClock } from "../../utils/datetime";
import { useCalendarData } from "../../hooks/useCalendarData";
import { useSelectedWeek } from "../../hooks/useSelectedWeek";
import {
  buildEventColumns,
  buildScheduledMatches,
  buildSlotLayout,
  computeConflicts,
  formatScoreLabel,
} from "./calendar/matchLayout";
import { WeekNavigator } from "./calendar/WeekNavigator";
import { WeekDaysGrid } from "./calendar/WeekDaysGrid";
import { ParticipantFilter } from "./calendar/ParticipantFilter";
import { CalendarTimeGrid } from "./calendar/CalendarTimeGrid";

export const GlobalMatchesCalendarPage: React.FC = () => {
  const { user } = useAuth();
  const canManage = !!(user && (user.can_manage_all || user.can_manage_content));

  const {
    season,
    seasonUsers,
    matches,
    participants,
    isLoading,
    participantsById,
    stagesById,
    eventsById,
    loadCalendar,
  } = useCalendarData();

  const [editingMatch, setEditingMatch] = useState<Match | null>(null);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [seasonModal, setSeasonModal] = useState(false);

  const selectedUserNumericId = selectedUserId ? Number(selectedUserId) : null;

  const filteredMatches = useMemo(() => {
    if (!selectedUserNumericId) return matches;
    return matches.filter((match) => {
      const player1 = match.player1 ? participantsById.get(match.player1) : null;
      const player2 = match.player2 ? participantsById.get(match.player2) : null;
      return (
        !!player1?.players.includes(selectedUserNumericId) ||
        !!player2?.players.includes(selectedUserNumericId)
      );
    });
  }, [matches, participantsById, selectedUserNumericId]);

  const scheduledMatches = useMemo(() => buildScheduledMatches(filteredMatches), [filteredMatches]);

  const {
    matchesByDay,
    weekDays,
    selectedDayKey,
    setSelectedDayKey,
    selectedDayMatches,
    selectedDate,
    weekRangeLabel,
    goPrevWeek,
    goNextWeek,
  } = useSelectedWeek(scheduledMatches);

  const selectedSeasonUser = useMemo(
    () => seasonUsers.find((seasonUser) => seasonUser.id === selectedUserNumericId) ?? null,
    [seasonUsers, selectedUserNumericId]
  );

  // Resetta il filtro se l'utente selezionato non è più tra gli iscritti.
  useEffect(() => {
    if (!selectedUserId) return;
    if (!seasonUsers.some((seasonUser) => seasonUser.id === Number(selectedUserId))) {
      setSelectedUserId("");
    }
  }, [seasonUsers, selectedUserId]);

  const conflictsByMatchId = useMemo(
    () => computeConflicts(selectedDayMatches, stagesById, participantsById),
    [selectedDayMatches, stagesById, participantsById]
  );
  const eventColumns = useMemo(
    () => buildEventColumns(selectedDayMatches, stagesById, eventsById),
    [selectedDayMatches, stagesById, eventsById]
  );
  const slotLayout = useMemo(() => buildSlotLayout(selectedDayMatches), [selectedDayMatches]);

  const exportDateBounds = useMemo(() => {
    if (!scheduledMatches.length) return { min: "", max: "" };
    return {
      min: scheduledMatches[0].dayKey,
      max: scheduledMatches[scheduledMatches.length - 1].dayKey,
    };
  }, [scheduledMatches]);

  const exportRows = useMemo<GlobalMatchesExportRow[]>(
    () =>
      scheduledMatches.map((item) => {
        const stage = stagesById.get(item.match.stage);
        const event = stage ? eventsById.get(stage.event) : null;
        const player1Name = item.match.player1 ? participantsById.get(item.match.player1)?.name ?? "TBD" : "TBD";
        const player2Name = item.match.player2 ? participantsById.get(item.match.player2)?.name ?? "TBD" : "TBD";
        return {
          eventId: event?.id ?? stage?.event ?? 0,
          eventName: event?.tournament.name ?? "Torneo",
          locationName: event?.location?.name ?? "Sede TBD",
          dayKey: item.dayKey,
          timeLabel: formatClock(item.start),
          player1Name,
          player2Name,
          stageName: stage?.name ?? `Stage #${item.match.stage}`,
          scoreLabel: formatScoreLabel(item.match),
        };
      }),
    [eventsById, participantsById, scheduledMatches, stagesById]
  );

  if (!currentTenant.enableCalendar) {
    return <p className="p-4 text-center text-gray-600">Calendario non disponibile per questo tenant.</p>;
  }

  if (isLoading) {
    return <p className="p-4 text-center text-gray-600">Caricamento calendario...</p>;
  }

  const handleExportPdf = async (range: { startDate: string; endDate: string }) => {
    if (!season) return;
    const blob = await exportCompetitionMatchesPdf({
      seasonId: season.id,
      startDate: range.startDate,
      endDate: range.endDate,
      userId: selectedUserNumericId,
      userLabel: selectedSeasonUser ? userFullName(selectedSeasonUser) : "Tutti gli iscritti",
    });
    downloadBlob(blob, `calendario_match_${range.startDate}_${range.endDate}.pdf`);
  };

  const handleExportExcel = async (range: { startDate: string; endDate: string }) => {
    if (!season) return;
    const blob = await exportCompetitionMatchesExcel({
      seasonId: season.id,
      startDate: range.startDate,
      endDate: range.endDate,
      userId: selectedUserNumericId,
      userLabel: selectedSeasonUser ? userFullName(selectedSeasonUser) : "Tutti gli iscritti",
    });
    downloadBlob(blob, `calendario_match_${range.startDate}_${range.endDate}.xlsx`);
  };

  return (
    <div className="my-6 space-y-5">
      <div className="flex justify-between items-center">
        <h1 className="text-xl font-bold">Calendario</h1>
        <Button variant="primary" onClick={() => setSeasonModal(true)}>
          {season?.name || "Seleziona"}
        </Button>
      </div>

      <WeekNavigator weekRangeLabel={weekRangeLabel} onPrev={goPrevWeek} onNext={goNextWeek} />

      <WeekDaysGrid
        weekDays={weekDays}
        selectedDayKey={selectedDayKey}
        matchesByDay={matchesByDay}
        onSelectDay={setSelectedDayKey}
      />

      <ParticipantFilter
        seasonUsers={seasonUsers}
        selectedUserId={selectedUserId}
        onChange={setSelectedUserId}
      />

      {selectedDate ? (
        <h2 className="px-1 text-lg font-semibold capitalize text-gray-900">
          {selectedDate.toLocaleDateString("it-IT", {
            weekday: "long",
            day: "2-digit",
            month: "long",
            year: "numeric",
          })}
        </h2>
      ) : null}

      {!selectedDayMatches.length && scheduledMatches.length ? (
        <div className="rounded border border-gray-300 bg-white p-4 text-center text-sm text-gray-500">
          Nessun match per il giorno selezionato.
        </div>
      ) : null}

      <CalendarTimeGrid
        eventColumns={eventColumns}
        slotLayout={slotLayout}
        conflictsByMatchId={conflictsByMatchId}
        participantsById={participantsById}
        stagesById={stagesById}
        eventsById={eventsById}
        canManage={canManage}
        onSelectMatch={setEditingMatch}
      />

      {!scheduledMatches.length ? (
        <div className="rounded border border-gray-300 bg-white p-4 text-center text-sm text-gray-500">
          Nessun match programmato{selectedUserNumericId ? " per il filtro selezionato" : ""}.
        </div>
      ) : null}

      <div className="bg-white border border-gray-300 rounded shadow-sm p-4">
        <div className="flex items-center justify-center gap-3">
          <Button variant="secondary" onClick={() => setIsExportModalOpen(true)} disabled={!exportRows.length}>
            Esporta calendario
          </Button>
        </div>
      </div>

      {editingMatch && (
        <MatchEditModal
          match={editingMatch}
          participants={participants}
          getSlotLabel={() => "TBD"}
          enableCalendar
          tournamentName={
            eventsById.get(stagesById.get(editingMatch.stage)?.event ?? -1)?.tournament.name
          }
          onClose={() => setEditingMatch(null)}
          onSaved={loadCalendar}
        />
      )}

      <GlobalMatchesExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        selectedUserLabel={selectedSeasonUser ? userFullName(selectedSeasonUser) : "Tutti gli iscritti"}
        dateBounds={exportDateBounds}
        rows={exportRows}
        onExportExcel={handleExportExcel}
        onExportPdf={handleExportPdf}
      />

      {seasonModal && season && (
        <ModalSelectSeason
          currentSeason={season}
          onSelect={(s) => {
            setSeasonModal(false);
            loadCalendar(s);
          }}
          onClose={() => setSeasonModal(false)}
        />
      )}
    </div>
  );
};
